import { useState, useEffect } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { Menu, X } from 'lucide-react'

const navLinks = [
  { label: 'Browse', href: '/browse', internal: false },
  { label: 'How It Works', href: '/how-it-works', internal: true },
  { label: 'About', href: '/about', internal: true },
]

// Shared marketing-site header (Landing, How It Works, About). Transparent
// over a hero on the home page, always solid on interior pages.
export default function MarketingHeader({ alwaysSolid = false }: { alwaysSolid?: boolean }) {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const solid = alwaysSolid || scrolled

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? 'bg-white/95 backdrop-blur-md border-b border-border shadow-sm' : 'bg-transparent'
      }`}
      // Pinned to the very top of the viewport, so on a notched device (installed
      // PWA / native shell) its contents would sit under the status bar now that
      // viewport-fit=cover is set. Resolves to 0 in a normal browser.
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center">
          <img
            src={solid ? '/IGotThat_logo_bk.svg' : '/IGotThat_logo_wh.svg'}
            alt="I Got That U.S."
            width={230}
            height={34}
            style={{ width: '230px', height: 'auto' }}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.internal ? (
              <NavLink
                key={link.label}
                to={link.href}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors hover:opacity-70 ${
                    isActive
                      ? solid ? 'text-primary' : 'text-accent'
                      : solid ? 'text-foreground' : 'text-white/90'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ) : (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm font-medium transition-colors hover:opacity-70 ${
                  solid ? 'text-foreground' : 'text-white/90'
                }`}
              >
                {link.label}
              </a>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/login"
            className={`text-sm font-medium transition-colors ${
              solid ? 'text-foreground hover:text-primary' : 'text-white/90 hover:text-white'
            }`}
          >
            Sign in
          </Link>
          <Link
            to="/register"
            className={`text-sm font-medium transition-colors ${
              solid ? 'text-foreground hover:text-primary' : 'text-white/90 hover:text-white'
            }`}
          >
            Sign up
          </Link>
          <Link
            to="/list-item"
            className="px-4 py-2 rounded-md bg-accent text-accent-foreground text-sm font-semibold hover:bg-amber-400 transition-colors"
          >
            List Your Item
          </Link>
        </div>

        <button
          className={`md:hidden ${solid ? 'text-foreground' : 'text-white'}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white border-t border-border px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) =>
            link.internal ? (
              <NavLink
                key={link.label}
                to={link.href}
                className="text-foreground font-medium text-sm py-1"
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </NavLink>
            ) : (
              <a key={link.label} href={link.href} className="text-foreground font-medium text-sm py-1">
                {link.label}
              </a>
            )
          )}
          <Link to="/login" className="text-foreground font-medium text-sm py-1" onClick={() => setMenuOpen(false)}>
            Sign in
          </Link>
          <Link to="/register" className="text-foreground font-medium text-sm py-1" onClick={() => setMenuOpen(false)}>
            Sign up
          </Link>
          <Link
            to="/list-item"
            className="mt-2 px-4 py-2 rounded-md bg-accent text-accent-foreground text-sm font-semibold text-center"
          >
            List Your Item
          </Link>
        </div>
      )}
    </header>
  )
}
